import { Button, Divider, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { flyToGeometry } from "../../../../components/VMRS/ExploreArchive/ultis";
import UnifiedDynamicForm from "../../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";
import { AOI_FEATURE_KEY } from "../../../../components/VMRS/MapAdapter/plugins/aoi/consts";
import { WorkflowStep } from "../../../../components/VMRS/Order/Tasking/types/workflow";
import { resolveOrderListCrumb } from "../../../../constants/routePaths";
import { ESttausCode } from "../../../../enums";
import { showError, showSuccess } from "../../../../helpers/toast";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import
{
   getDetailOrderTaskingByUerAsync,
   getSchemaOrderAsync,
   updateOrderAsync,
} from "../../../../store/reducers/orderSlice";
import type { AppDispatch, RootState } from "../../../../store/store";

import CommonComponents from "../../../../components/Commons";
import
{
   transformFormDataToOrderPayload,
   transformOrderToFormData,
} from "../../../../components/VMRS/Order/Tasking/editFormData";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { changeSourceData } from "../../../../components/VMRS/MapAdapter/plugins/draw";
import
{
   clearFeatures,
   updateFeature,
} from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import
{
   changeBasemap,
   setBasemapList,
} from "../../../../components/VMRS/MapAdapter/store/slices/mapSlice";
import { BASEMAPS } from "../../../../components/VMRS/MapAdapter/utils/consts";
import { loadBasemaps } from "../../../../components/VMRS/MapAdapter/store/thunks/loadBasemaps";
import useToggle from "../../../../hooks/useToggle";
import { DRAW_SOURCE_ID } from "../../../../components/VMRS/MapAdapter/plugins/draw/consts";
import { map } from "lodash";

export default function EditOrderTasking()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch<AppDispatch>();
   const navigate = useNavigate();
   const location = useLocation();
   // Mounted under both the tasking and the monitoring list — the crumbs follow
   // whichever list the current URL belongs to.
   const listCrumb = resolveOrderListCrumb(location.pathname);
   const [data, setData] = useState<any>({});
   // The form as the order was loaded — what "changed" is measured against.
   const [initialFormData, setInitialFormData] = useState<any>({});
   const [schema, setSchema] = useState<any>({});
   const [loadingSchema, setLoadingSchema] = useState(true);
   const [loading, setLoading] = useState(false);
   const [formData, setFormData] = useState<Record<string, any>>({});
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(
      undefined,
   );

   const {
      mapType,
      view: mapView,
      basemaps,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);
   const { features } = useSelector((state: RootState) => state.draw);

   const {
      open: openConfirm,
      toggle: toggleConfirm,
      shouldRender: shouldConfirm,
   } = useToggle();

   const getData = async () =>
   {
      try
      {
         const result = await dispatch(
            getDetailOrderTaskingByUerAsync(id as string),
         );
         const res = result as any;
         setData(res?.payload?.data?.data ?? {});

         if (res?.payload?.data?.data?.items?.[0]?.order_payload?.geometry)
         {
            const geometry =
               res?.payload?.data?.data?.items?.[0]?.order_payload?.geometry;
            const featureId = `${AOI_FEATURE_KEY}-${res?.payload?.data?.data?.order_number}`;
            const fill = "rgba(255, 255, 0, 0.9)";
            const stroke = "rgba(255, 255, 0, 1)";



            dispatch(updateFeature({ id: featureId, geometry, fill, stroke }));
            changeSourceData(engineMap, [{ id: featureId, geometry, fill, stroke }]);
            flyToGeometry(geometry, engineMap);
         }

         handleGetSchema(res?.payload?.data?.data);
      } catch (error)
      {
         console.error("Failed to fetch order data:", error);
      } finally
      {
         setLoading(false);
      }
   };


   // Validation: returns { isValid: boolean, errors: string[] }
   const getValidationErrors = () =>
   {
      const errors: string[] = [];

      if (!schema?.steps) return errors;

      schema.steps.forEach((step: any) =>
      {
         const uiOptions = step?.ui?.options || {};
         // Required fields come from ui.options[key].required (not schema.required).
         const requiredFields = Object.keys(uiOptions).filter(
            (key) => uiOptions[key]?.required === true,
         );

         requiredFields.forEach((fieldKey: string) =>
         {
            const value = formData[fieldKey];
            const fieldConfig = uiOptions[fieldKey];
            const fieldLabel = fieldConfig?.title || fieldKey;

            // check empty
            const isEmpty =
               value === undefined ||
               value === null ||
               value === "" ||
               (Array.isArray(value) && value.length === 0) ||
               (typeof value === "object" &&
                  !Array.isArray(value) &&
                  Object.keys(value).length === 0);

            if (isEmpty)
            {
               errors.push(`${t(fieldLabel)} ${t("is required")}`);
               return;
            }

            // special case: date range
            if (fieldKey === "date_from" || fieldKey === "date_to")
            {
               const fromVal = formData?.date_from;
               const toVal = formData?.date_to;

               if (fromVal && toVal)
               {
                  const fromDate = dayjs(fromVal);
                  const toDate = dayjs(toVal);

                  if (fromDate.isAfter(toDate))
                  {
                     errors.push(t("Date from must be before date to"));
                  }
               }
            }
         });
      });

      return errors;
   };

   // What a save would send. Recomputed as the form changes so the button can say
   // up front that there is nothing to send, instead of only finding out on click.
   const changedFields = useMemo(
      () => transformFormDataToOrderPayload(formData, initialFormData, schema),
      [formData, initialFormData, schema],
   );

   const hasChanges = Object.keys(changedFields).length > 0;

   const validationErrors = useMemo(
      () => getValidationErrors(),
      [formData, schema],
   );
   const isValid = validationErrors.length === 0;

   const handleGetSchema = async (data: any) =>
   {
      try
      {
         setLoadingSchema(true);

         const providerId = data?.items?.[0]?.provider_code;

         if (!providerId)
         {
            console.warn("Provider ID not found in order data");
            setLoadingSchema(false);
            return;
         }

         const res = await dispatch(
            // @ts-ignore
            getSchemaOrderAsync({
               provider_id: providerId,
               type: "order_tasking",
            }),
         ).unwrap();
         setSchema(res?.data?.data?.workflow ?? {});
         setLoadingSchema(false);
      } catch (error)
      {
         console.error("Failed to fetch schema:", error);
         setLoadingSchema(false);
         showError(t("Failed to load form schema"));
      } finally
      {
         setLoading(false);
      }
   };

   const handleConfirm = async () =>
   {
      toggleConfirm();
      try
      {
         const payload = transformFormDataToOrderPayload(
            formData,
            initialFormData,
            schema,
            { debug: true },
         );

         // The button is already disabled in this case; this only guards against
         // a save slipping through some other way.
         if (Object.keys(payload).length === 0) return;

         setLoading(true);

         const result = await dispatch(
            updateOrderAsync({ ...payload, id: id as string }),
         );
         const res = result as any;

         if (
            res?.payload?.status === ESttausCode.OK ||
            res?.payload?.status === ESttausCode.CREATED
         )
         {
            showSuccess(t("Update successfully"));
            navigate(-1);
         } else
         {
            showError(t("Update failed"));
         }
      } catch (error)
      {
         console.error("Failed to update order:", error);
         showError(t("Update failed"));
      } finally
      {
         setLoading(false);
      }
   };

   // Why the save button can't be pressed, or null when it can. Missing fields
   // come first: they have to be filled before "nothing changed" even applies.
   const saveBlockedReason = !isValid ? (
      <ul className="list-disc pl-4">
         {validationErrors.map((err, idx) => (
            <li key={idx}>{err}</li>
         ))}
      </ul>
   ) : !hasChanges ? (
      t("No changes yet")
   ) : null;

   const drawSource = engineMap?.getSource(DRAW_SOURCE_ID);


   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(() =>
   {
      if (!engineMap || !id) return;

      getData();
   }, [id, engineMap]);



   useEffect(() =>
   {
      return () =>
      {
         dispatch(clearFeatures());
      };
   }, []);

   useEffect(() =>
   {
      // @ts-ignore – thunk
      dispatch(loadBasemaps());
   }, []);

   // When schema loads, re-transform form data
   // eslint-disable-next-line react-hooks/exhaustive-deps
   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(() =>
   {
      if (
         schema &&
         Object.keys(schema).length > 0 &&
         data &&
         Object.keys(data).length > 0
      )
      {
         const initial = transformOrderToFormData(data, schema);

         setFormData(initial);
         setInitialFormData(initial);
      }
   }, [schema, data]);

   // Build formStep from workflow schema
   const formSteps: WorkflowStep[] = useMemo(() =>
   {
      if (!schema || !schema.steps) return [];

      return schema.steps
         .filter((step: any) => step?.schema && step?.ui)
         .map((step: any) => ({
            title: step?.title,
            step_id: step.step_id,
            schema: {
               // Keep required in sync with ui.options[key].required (single source of truth).
               required: Object.keys(step.ui?.options || {}).filter(
                  (key: string) => step.ui?.options?.[key]?.required === true,
               ),
            },
            ui: {
               order: step.ui?.order || [],
               options: step.ui?.options || {},
            },
         }));
   }, [schema]);

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t(listCrumb.labelKey),
                     path: `${listCrumb.path}${location.search}`,
                  },
                  {
                     title: data?.name,
                     path: `${listCrumb.path}/${data?.id}${location.search}`,
                  },
                  {
                     title: t("Edit"),
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            {/* Left Panel - Form */}

            <div className="h-full relative w-5/12 flex flex-col border-r border-[#26324a]">
               {/* Scrollable Form Container */}

               <Spin spinning={loadingSchema || loading}>
                  <div
                     className="pt-6 overflow-auto"
                     style={{ height: "calc(100% - 65px)" }}
                  >
                     {loadingSchema ? (
                        <div className="p-4 text-gray-400 text-center">
                           {t("Loading form...")}
                        </div>
                     ) : formSteps.length > 0 && !loadingSchema ? (
                        formSteps.map((step, index) => (
                           <div key={step?.step_id}>
                              <p className="text-xl font-semibold px-6 pb-4">
                                 {t(step?.title)}
                              </p>
                              <UnifiedDynamicForm
                                 step={step}
                                 formData={formData}
                                 setFormData={setFormData}
                                 type="edit"
                                 display="grid"
                              />

                              {index !== formSteps.length - 1 && <Divider />}
                           </div>
                        ))
                     ) : (
                        <div className="p-4 text-gray-400 text-center">
                           {t("No form schema available")}
                        </div>
                     )}
                  </div>
               </Spin>
               <div
                  className="flex absolute bottom-0 w-full justify-between px-6  py-4"
                  style={{ borderTop: "1px solid var(--divider-1)" }}
               >
                  <div>
                     <span className="w-[151px] block">
                        <Button
                           onClick={() => navigate(-1)}
                           className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                           style={{ background: "none" }}
                        >
                           {t("Back")}
                        </Button>
                     </span>
                  </div>
                  <div className="flex gap-2">
                     {saveBlockedReason ? (
                        // AntD swallows hover events on a disabled button, so the
                        // tooltip hangs off the wrapper instead — otherwise the
                        // button just sits there dead with no explanation.
                        <Tooltip title={saveBlockedReason} placement="top">
                           <span className="inline-block cursor-not-allowed">
                              <Button className="btn-primary" disabled>
                                 {t("Save request")}
                              </Button>
                           </span>
                        </Tooltip>
                     ) : (
                        <Button
                           className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                           onClick={() => toggleConfirm()}
                           disabled={loadingSchema || !formSteps}
                        >
                           {t("Save request")}
                        </Button>
                     )}
                  </div>
               </div>
            </div>

            {/* Right Panel - Map */}
            <div className="flex-1 relative">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        // setEngineMap only after map is fully loaded so fly2GeometryList has a ready engine
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  />
               )}
            </div>

            {shouldConfirm && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openConfirm}
                  toggle={toggleConfirm}
                  handleConfirm={handleConfirm}
               >
                  <p>{t("Are you sure you want to save this request?")}</p>
               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
